import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import isEqual from 'lodash/isEqual';
import { History, LocationState } from 'history';
import Select, { StylesConfig } from 'react-select';
import {
  Form,
  FormGroup,
  FormControl,
  Label,
  Glyphicon,
  Col,
  Image,
} from 'react-bootstrap';
import styled from 'styled-components';
import { ColorResult } from 'react-color';
import cloneDeep from 'lodash/cloneDeep';
import uniqBy from 'lodash/uniqBy';

import {
  Itinerary,
  ItineraryDaySection,
  ItineraryDay,
} from '../../../../types/Itinerary';
import { City } from '../../../../types/City';
import ColorPicker from '../../../../components/ColorPicker';
import { Activity } from '../../../../types/Activity';
import { getCityBySlug, getActivityByTag } from '../../../../api/ActivitiesApi';
import { useFetchData } from '../../../../infrastructure/hooks/useFetchData';
import { ApiStatus } from '../../../../infrastructure/constants/apiStatus';
import Header from '../Header';
import { useAppDispatch } from '../../../../store';
import { DEFAULT_COLOR } from '../ItineraryTemplateCreatePage/ItineraryTemplateCreatePage';

const images = [
  'https://ik.imagekit.io/bi5ihkjoby38/prod-city-guides/itineraries/batch/adventureMountain.png',
  'https://ik.imagekit.io/bi5ihkjoby38/prod-city-guides/itineraries/batch/discoBall.png',
  'https://ik.imagekit.io/bi5ihkjoby38/prod-city-guides/itineraries/batch/flamingoFloatie.png',
];

const FormLabel = styled.label`
  font-size: 18px;
`;

const Title = styled(Label)`
  font-size: 18px;
`;

type RowProps = {
  alignItems?: string;
};

const Row = styled.div<RowProps>`
  display: flex;
  flex-direction: row;
  align-items: ${({ alignItems }) => (alignItems ? alignItems : 'baseline')};
  label {
    min-width: 100px;
    margin-right: 10px;
  }
`;

type DaysRowProps = {
  isEmpty?: boolean;
};

const DaysRow = styled.div<DaysRowProps>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ isEmpty }) => (isEmpty ? 'center' : 'flex-start')};
  align-items: baseline;
  margin-top: 30px;
`;

const IllustrationRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const ColumnRow = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 10px;
`;

const Section = styled.div``;

const DEFAULT_SECTIONS: ItineraryDaySection[] = [
  { title: 'Morning', events: [] },
  { title: 'Afternoon', events: [] },
  { title: 'Evening', events: [] },
];

type ItinerarySectionProps = {
  item: ItineraryDaySection;
  day: string;
  activities: Activity[];
  handleChangeSectionEvents(
    day: string,
    section: string,
    activity: Activity
  ): void;
};

const getOptionLabel = (activity?: Activity): string => {
  const { title = '' } = activity ?? {};

  return title;
};
const getOptionValue = (activity?: Activity): string => {
  const { id = '' } = activity ?? {};
  return id;
};

const styles: StylesConfig<Activity, false> = {
  control: styles => ({
    ...styles,
    width: 250,
    marginBottom: 20,
  }),
};

const ItinerarySection = memo(
  ({
    item,
    activities = [],
    handleChangeSectionEvents,
    day,
  }: ItinerarySectionProps) => {
    const { title, events = [] } = item;
    const { activity } = events.find(item => item.activity) ?? {};
    return (
      <Section>
        <FormLabel>{title}</FormLabel>
        <Select
          styles={styles}
          placeholder={'Start typing to select'}
          isSearchable
          options={activities}
          isLoading={!activities.length}
          onChange={value => {
            handleChangeSectionEvents(day, title, value as Activity);
          }}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          value={activity}
          isClearable
          cacheOptions
        />
      </Section>
    );
  }
);

type ItineraryDayProps = {
  item: ItineraryDay;
  activities: Activity[];
  onDeleteDay(item: ItineraryDay): void;
  handleChangeSectionEvents(
    day: string,
    section: string,
    activity: Activity
  ): void;
  index: number;
};

const ItineraryDayItem = memo(
  ({
    item,
    onDeleteDay,
    activities = [],
    handleChangeSectionEvents,
    index,
  }: ItineraryDayProps) => {
    const { title, sections = [] } = item;
    const renderSections = (item: ItineraryDaySection) => {
      return (
        <ItinerarySection
          key={item.title}
          day={title}
          handleChangeSectionEvents={handleChangeSectionEvents}
          item={item}
          activities={activities}
        />
      );
    };

    return (
      <ColumnRow>
        <FormLabel>
          {index + 1} Day{' '}
          <i
            style={{ color: 'red' }}
            onClick={() => {
              onDeleteDay(item);
            }}
            className='fas fa-times'
          />
        </FormLabel>
        {uniqBy([...sections, ...DEFAULT_SECTIONS], 'title').map(
          renderSections
        )}
      </ColumnRow>
    );
  }
);

type Props = {
  history: History<LocationState>;
  itineraryTemplate: Partial<Itinerary>;
  onSave(template: Partial<Itinerary>): void;
};

const TemplateForm = ({ history, itineraryTemplate, onSave }: Props) => {
  const dispatch = useAppDispatch();
  const { goBack } = history;

  const [template, setTemplate] = useState<Partial<Itinerary>>(
    itineraryTemplate
  );

  const [activitiesApiStatus, activities = [], fetchActivities] = useFetchData<
    Activity[]
  >();

  const [, city, fetchCity] = useFetchData<City>();

  const { name } = city ?? {};

  const {
    citySlug = '',
    vibe = '',
    title = '',
    subtitle = '',
    color = DEFAULT_COLOR,
    itineraryDays = [],
    image = '',
  } = template ?? {};

  const disable = useMemo(() => {
    return (
      !vibe.length ||
      !title.length ||
      !subtitle.length ||
      !color.length ||
      !image.length ||
      !itineraryDays.length ||
      !itineraryDays.every(item =>
        item.sections.some(item => item.events.some(item => !!item.activity))
      ) ||
      isEqual(itineraryTemplate, template)
    );
  }, [
    vibe.length,
    title.length,
    subtitle.length,
    color.length,
    image.length,
    itineraryDays,
    itineraryTemplate,
    template,
  ]);

  const onSetTemplateFields = useCallback(
    (fields: Partial<Itinerary>) => {
      setTemplate(prev => ({ ...prev, ...fields }));
    },
    [setTemplate]
  );

  const getSelectedActivities = useCallback(
    (index: number) => {
      const { sections = [] } = itineraryDays[index] ?? {};
      if (!sections.length) return activities;
      const set = new Set(
        sections.reduce((acc: string[], currentSection) => {
          const { events = [] } = currentSection;
          for (const { activity } of events) {
            if (activity) {
              const { id } = activity;
              acc.push(id);
            }
          }
          return acc;
        }, [])
      );
      return activities.filter(({ id }) => !set.has(id));
    },
    [activities, itineraryDays]
  );

  const handleChangeSectionEvents = useCallback(
    (day: string, section: string, activity: Activity) => {
      const updatedItineraryDays: ItineraryDay[] = cloneDeep(itineraryDays);
      const dayIndex = updatedItineraryDays.findIndex(
        item => item.title === day
      );

      const sectionIndex = updatedItineraryDays[dayIndex].sections.findIndex(
        item => item.title === section
      );

      if (sectionIndex >= 0) {
        updatedItineraryDays[dayIndex].sections[sectionIndex].events = [
          { activity },
        ];
      } else {
        updatedItineraryDays[dayIndex].sections.push({
          title: section,
          events: [{ activity }],
        });
      }

      onSetTemplateFields({ itineraryDays: updatedItineraryDays });
    },
    [itineraryDays, onSetTemplateFields]
  );

  const handleChangeComplete = (color: ColorResult) => {
    const { hex } = color;
    onSetTemplateFields({ color: hex });
  };

  const onAddDay = useCallback(() => {
    const updatedItineraryDays: ItineraryDay[] = cloneDeep(itineraryDays);

    const day: ItineraryDay = {
      title: `Day ${itineraryDays.length + 1}`,
      sections: DEFAULT_SECTIONS,
    };
    updatedItineraryDays.push(day);

    onSetTemplateFields({ itineraryDays: updatedItineraryDays });
  }, [itineraryDays, onSetTemplateFields]);

  const onDeleteDay = useCallback(
    (item: ItineraryDay) => {
      const filteredItineraryDays = [...itineraryDays].filter(
        day => day.title !== item.title
      );
      onSetTemplateFields({ itineraryDays: filteredItineraryDays });
    },
    [itineraryDays, onSetTemplateFields]
  );

  const renderDays = useCallback(
    (item: ItineraryDay, index: number) => {
      return (
        <ItineraryDayItem
          key={item.title}
          index={index}
          activities={getSelectedActivities(index)}
          item={item}
          onDeleteDay={onDeleteDay}
          handleChangeSectionEvents={handleChangeSectionEvents}
        />
      );
    },
    [getSelectedActivities, handleChangeSectionEvents, onDeleteDay]
  );
  const renderImages = (src: string) => {
    return (
      <Col xs={6} md={4} key={src}>
        {src === image && (
          <Glyphicon
            style={{ color: 'green', position: 'absolute' }}
            glyph='ok'
          />
        )}
        <Image
          onClick={() => {
            onSetTemplateFields({ image: src });
          }}
          width={150}
          src={src}
          roundedCircle
        />
      </Col>
    );
  };

  useEffect(() => {
    let ignore = false;
    const initCity = async () => {
      if (!ignore) fetchCity(() => getCityBySlug(citySlug));
    };
    const initActivities = async () => {
      if (name && !ignore) fetchActivities(() => getActivityByTag(name));
    };
    initActivities();
    initCity();
    return () => {
      ignore = false;
    };
  }, [name, citySlug, dispatch, fetchActivities, fetchCity]);

  useLayoutEffect(() => {
    if (itineraryDays && itineraryDays.length) {
      const isTitlesValid = itineraryDays.every(
        (day, index) => day.title === `Day ${index + 1}`
      );

      if (!isTitlesValid) {
        const updatedDays = itineraryDays.map((day, index) => ({
          ...day,
          title: `Day ${index + 1}`,
        }));
        onSetTemplateFields({ itineraryDays: updatedDays });
      }
    }
  }, [itineraryDays, onSetTemplateFields]);

  return (
    <>
      <Header
        goBack={goBack}
        name={title}
        onSave={() => onSave(template)}
        disabled={disable}
      />
      <div className='data-container'>
        <div className='data-box'>
          <div className='media'>
            <h2 className='media-heading'>
              <b>{title}</b>
            </h2>
          </div>
        </div>
        <div className='data-box'>
          <Form>
            <FormGroup className='mb-3'>
              <Row>
                <FormLabel>Vibe</FormLabel>
                <FormControl
                  value={vibe}
                  placeholder='Enter Vibe'
                  onChange={({ target: { value } }) =>
                    onSetTemplateFields({ vibe: value })
                  }
                />
              </Row>
            </FormGroup>
            <FormGroup className='mb-3'>
              <Row>
                <FormLabel>Title</FormLabel>
                <FormControl
                  value={title}
                  placeholder='Enter Title'
                  onChange={({ target: { value } }) =>
                    onSetTemplateFields({ title: value })
                  }
                />
              </Row>
            </FormGroup>
            <FormGroup className='mb-3'>
              <Row>
                <FormLabel>Subtitle</FormLabel>
                <FormControl
                  value={subtitle}
                  onChange={({ target: { value } }) =>
                    onSetTemplateFields({ subtitle: value })
                  }
                  placeholder='Enter Subtitle'
                />
              </Row>
            </FormGroup>
            <FormGroup className='mb-3'>
              <Row alignItems='flex-start'>
                <FormLabel>Color</FormLabel>
                <ColorPicker
                  onChange={handleChangeComplete}
                  onChangeComplete={handleChangeComplete}
                  color={color}
                  triangle='hide'
                />
              </Row>
            </FormGroup>
            <FormGroup className='mb-3'>
              <FormLabel>Illustration</FormLabel>
              <IllustrationRow>{images.map(renderImages)}</IllustrationRow>
            </FormGroup>
            <DaysRow isEmpty={!activities.length}>
              {activitiesApiStatus === ApiStatus.loading ? (
                <Glyphicon
                  key={0}
                  glyph='refresh'
                  className='spinning'
                  style={{ fontSize: '14px' }}
                />
              ) : !activities.length ? (
                <Title>
                  There are no activities associated with this city.
                </Title>
              ) : (
                <>
                  {itineraryDays.map(renderDays)}
                  <button
                    type='button'
                    onClick={onAddDay}
                    className='btn btn-def'
                  >
                    + Add Day
                  </button>
                </>
              )}
            </DaysRow>
          </Form>
        </div>
      </div>
    </>
  );
};
export default TemplateForm;
